/* Gráficos em SVG puro (sem biblioteca): barras empilhadas, linhas, rosca e barras horizontais. */

import { esc, MESES, nf, num, pct } from '../core/formato';
import type { Mes } from '../tipos/api';
import { vazio } from './componentes';

const CORES_ESCOPO = ['var(--esc1)', 'var(--esc2)', 'var(--esc3)'];

function grade(max: number, y: (v: number) => number, esquerda: number, largura: number, direita: number): string {
  let g = '';
  for (let i = 0; i <= 4; i++) {
    const v = (max * i) / 4, yy = y(v);
    g += `<line x1="${esquerda}" x2="${largura - direita}" y1="${yy}" y2="${yy}" stroke="var(--line-2)" stroke-width="1"/>
          <text x="${esquerda - 10}" y="${yy + 4}" text-anchor="end" class="bar-tip">${nf(v, 0)}</text>`;
  }
  return g;
}

/** Barras mensais empilhadas por escopo; cada item é [escopo1, escopo2, escopo3]. */
export function barrasEscopo(dados: number[][], h = 260): string {
  const W = 900, pad = { l: 48, r: 14, t: 14, b: 32 };
  const max = Math.max(1, ...dados.map((d) => d[0] + d[1] + d[2])) * 1.12;
  const bw = (W - pad.l - pad.r) / dados.length;
  const y = (v: number) => pad.t + (h - pad.t - pad.b) * (1 - v / max);
  let g = grade(max, y, pad.l, W, pad.r);
  dados.forEach((d, i) => {
    const x = pad.l + i * bw + bw * 0.22, w = bw * 0.56;
    let acc = 0;
    d.forEach((v, k) => {
      const y0 = y(acc + v), y1 = y(acc);
      g += `<rect x="${x}" y="${y0}" width="${w}" height="${Math.max(y1 - y0, 0)}" fill="${CORES_ESCOPO[k]}"${k === 2 ? ' rx="3"' : ''}><title>${MESES[i]} · Escopo ${k + 1}: ${nf(v)} tCO₂e</title></rect>`;
      acc += v;
    });
    g += `<text x="${x + w / 2}" y="${h - 12}" text-anchor="middle" class="bar-tip">${MESES[i]}</text>`;
  });
  return `<svg viewBox="0 0 ${W} ${h}" style="width:100%;height:auto" role="img" aria-label="Emissões mensais por escopo">${g}</svg>`;
}

/** Linhas acumuladas por escopo e total. */
export function linhasEscopo(dados: Mes[], h = 260): string {
  const W = 900, pad = { l: 52, r: 18, t: 14, b: 32 };
  const series: [keyof Mes, string, string][] = [
    ['escopo_1', 'var(--esc1)', 'Escopo 1'], ['escopo_2', 'var(--esc2)', 'Escopo 2'],
    ['escopo_3', 'var(--esc3)', 'Escopo 3'], ['total_emissoes', 'var(--serie-total)', 'Total'],
  ];
  const max = Math.max(1, ...dados.map((d) => num(d.total_emissoes))) * 1.1;
  const x = (i: number) => pad.l + (W - pad.l - pad.r) * (i / 11);
  const y = (v: number) => pad.t + (h - pad.t - pad.b) * (1 - v / max);
  let g = grade(max, y, pad.l, W, pad.r);
  MESES.forEach((m, i) => (g += `<text x="${x(i)}" y="${h - 12}" text-anchor="middle" class="bar-tip">${m}</text>`));
  series.forEach(([k, c, l]) => {
    const total = k === 'total_emissoes';
    const pts = dados.map((d, i) => `${x(i)},${y(num(d[k]))}`).join(' ');
    g += `<polyline points="${pts}" fill="none" stroke="${c}" stroke-width="${total ? 2.5 : 2}" ${total ? 'stroke-dasharray="6 4"' : ''}/>`;
    dados.forEach((d, i) => (g += `<circle cx="${x(i)}" cy="${y(num(d[k]))}" r="3" fill="${c}"><title>${MESES[i]} · ${l}: ${nf(d[k])} tCO₂e</title></circle>`));
  });
  return `<svg viewBox="0 0 ${W} ${h}" style="width:100%;height:auto" role="img" aria-label="Emissões acumuladas">${g}</svg>`;
}

export interface Fatia {
  l: string;
  v: number;
  c: string;
}

export function donut(partes: Fatia[], size = 200): string {
  const total = partes.reduce((a, p) => a + p.v, 0);
  const r = size / 2 - 14, cx = size / 2, cy = size / 2, C = 2 * Math.PI * r;
  let off = 0;
  let g = total ? '' : `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="var(--line)" stroke-width="26"/>`;
  if (total) {
    partes.forEach((p) => {
      const len = C * (p.v / total);
      if (len <= 0) return;
      g += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${p.c}" stroke-width="26"
        stroke-dasharray="${Math.max(len - 2, 0)} ${C - len + 2}" stroke-dashoffset="${-off}" transform="rotate(-90 ${cx} ${cy})"
        stroke-linecap="butt"><title>${p.l}: ${nf(p.v)} tCO₂e (${pct(p.v, total)})</title></circle>`;
      off += len;
    });
  }
  return `<svg viewBox="0 0 ${size} ${size}" style="width:${size}px;height:${size}px">${g}
    <text x="${cx}" y="${cy - 2}" text-anchor="middle" font-size="22" font-weight="800" fill="var(--ink)" class="num">${nf(total, 0)}</text>
    <text x="${cx}" y="${cy + 18}" text-anchor="middle" font-size="11" font-weight="600" fill="var(--muted)">tCO₂e</text></svg>`;
}

export interface Barra {
  l: string;
  v: number;
  c?: string;
}

export function barrasH(itens: Barra[], unidade = 'tCO₂e'): string {
  if (!itens.length) return vazio();
  const max = Math.max(...itens.map((i) => i.v)) || 1;
  return `<div style="display:flex;flex-direction:column;gap:14px">${itens.map((i) => `
    <div>
      <div class="between" style="margin-bottom:6px"><span style="font-weight:600;font-size:13.5px">${esc(i.l)}</span>
      <span class="num" style="font-weight:700;font-size:13.5px;white-space:nowrap">${nf(i.v)} ${unidade}</span></div>
      <div style="height:9px;background:var(--surface);border-radius:99px;overflow:hidden">
        <i style="display:block;height:100%;width:${((i.v / max) * 100).toFixed(1)}%;background:${i.c || 'var(--marca-500)'};border-radius:99px"></i></div>
    </div>`).join('')}</div>`;
}

/** Barras horizontais empilhadas por escopo; v = [escopo1, escopo2, escopo3]. */
export function barrasEmpilhadas(itens: { l: string; v: number[] }[]): string {
  if (!itens.length) return vazio();
  const soma = (v: number[]) => v[0] + v[1] + v[2];
  const max = Math.max(...itens.map((i) => soma(i.v))) || 1;
  return `<div style="display:flex;flex-direction:column;gap:14px">${itens.map((i) => {
    const t = soma(i.v);
    return `<div>
      <div class="between" style="margin-bottom:6px"><span style="font-weight:600;font-size:13.5px">${esc(i.l)}</span>
        <span class="num" style="font-weight:700;font-size:13.5px;white-space:nowrap">${nf(t)} tCO₂e</span></div>
      <div class="stack" style="width:${((t / max) * 100).toFixed(1)}%">${i.v.map((v, k) => `<i style="width:${t ? ((v / t) * 100).toFixed(2) : 0}%;background:${CORES_ESCOPO[k]}" title="Escopo ${k + 1}: ${nf(v)} tCO₂e"></i>`).join('')}</div>
    </div>`;
  }).join('')}</div>`;
}
